import React, { useState } from 'react';
import { Truck, RefreshCw, AlertTriangle, Package, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useDistribution } from './hooks/useDistribution';
import { useAuth } from '@/context/AuthContext';
import { DistributionSuggestions } from './components/DistributionSuggestions';
import { TransfersList } from './components/TransfersList';
import { ManualTransferPage } from './components/ManualTransferPage';

export const DistributionPage: React.FC = () => {
  const { user, selectedLocation } = useAuth();

  // Check if user is CAJERO (positionId: 3)
  const isCajero = user?.positionId === 3;

  // For CAJERO users, default to transfers tab and only show that tab
  const [activeTab, setActiveTab] = useState<'overview' | 'suggestions' | 'transfers' | 'manual'>(
    isCajero ? 'transfers' : 'suggestions'
  );
  
  const {
    suggestions,
    transfers,
    loading,
    initialLoading,
    error,
    transferFilters,
    refreshData,
    generateSuggestions,
    createDistributionOrder,
    createManualTransfer,
    updateTransferFilters,
    searchTransfers
  } = useDistribution({ isCajero, locationId: selectedLocation?.id?.toString() });

  const handleRefreshData = async () => {
    await refreshData();
  };

  const handleGenerateSuggestions = async () => {
    await generateSuggestions();
  };

  if (initialLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-semibold text-gray-700">Analizando inventarios...</h2>
          <p className="text-gray-500 mt-2">Calculando sugerencias de distribución</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-700">Error al cargar datos</h2>
          <p className="text-red-600 mt-2">{error}</p>
          <Button 
            onClick={handleRefreshData}
            className="mt-4 bg-red-600 hover:bg-red-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-7 h-7 text-macartur-primary" />
            Centro de Distribución
          </h1>
          <p className="text-gray-600 mt-1">
            Gestión inteligente de inventarios multi-local
          </p>
        </div>
        
        {!isCajero && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleRefreshData}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Button
              onClick={handleGenerateSuggestions}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
              disabled={loading}
            >
              <Package className="w-4 h-4 mr-2" />
              Generar Sugerencias
            </Button>
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          {[
            // For CAJERO users, only show transfers tab
            ...(isCajero
              ? [{ id: 'transfers', label: 'Transferencias', icon: Truck, badge: transfers.filter(t => ['pending', 'in_transit'].includes(t.status)).length }]
              : [
                  { id: 'suggestions', label: 'Sugerencias', icon: Package, badge: suggestions.length },
                  { id: 'transfers', label: 'Transferencias', icon: Truck, badge: transfers.filter(t => ['pending', 'in_transit'].includes(t.status)).length },
                  { id: 'manual', label: 'Transferencia Manual', icon: Plus }
                ]
            )
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-macartur-primary text-macartur-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.badge != null && tab.badge > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {tab.badge}
                  </Badge>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'suggestions' && (
          <DistributionSuggestions
            suggestions={suggestions}
            onCreateOrder={createDistributionOrder}
            loading={loading}
          />
        )}

        {activeTab === 'transfers' && (
          <TransfersList
            transfers={isCajero ? transfers.filter(t => ['in_transit', 'received'].includes(t.status)) : transfers}
            loading={loading}
            isCajero={isCajero}
            filters={transferFilters}
            onFilterChange={updateTransferFilters}
            onSearch={(overrides) => searchTransfers(overrides)}
          />
        )}

        {activeTab === 'manual' && (
          <ManualTransferPage
            onCreateTransfer={async (data) => {
              const result = await createManualTransfer(data);
              if (result.success) {
                setActiveTab('transfers');
              }
              return result;
            }}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};